import { createHash, randomUUID } from "node:crypto";
import { mkdir, readFile, writeFile } from "node:fs/promises";
import path from "node:path";
import type { TablesClient } from "@databricks/sdk-uc-tables/v1";
import {
  type AttachmentRef,
  EmptySandboxEnv,
  type FabricPrincipal,
  type JsonObject,
  evaluateToolCallPolicy,
  runWithSubmissionContext,
} from "@fabric-harness/sdk";
import { databricksAgentServices } from "./agent-services.js";
import { ensureDatabricksAiGateway, listDatabricksModelServices } from "./ai-gateway.js";
import { digestDatabricksArtifact } from "./artifact-digest.js";
import { createDatabricksAuthoringCertificationChecks } from "./authoring-certification.js";
import type { DatabricksAuthoringCertificationFixtures } from "./authoring-certification.js";
import { databricksCertificationResourceName } from "./certification-resource.js";
import { runDatabricksCertification } from "./certification.js";
import type {
  DatabricksCertificationBlockingTier,
  DatabricksCertificationCheck,
} from "./certification.js";
import { DATABRICKS_API_VERSIONS, databricksCompatibilityRecord } from "./compatibility.js";
import { databricksConsumption } from "./consumption.js";
import { databricksActualCostSource, reconcileDatabricksCost } from "./cost-budget-actuals.js";
import { databricksFeatureLookupTool } from "./feature-serving.js";
import { databricksGenieAgentMode } from "./genie-agent-mode.js";
import { databricksGenieTool } from "./genie.js";
import { databricksGovernancePolicy, withGovernance } from "./governance.js";
import {
  databricksCredentials,
  databricksCredentialsFromTokenProvider,
  databricksIdentity,
  databricksPrincipalFromEnv,
} from "./identity.js";
import { databricksJobs } from "./jobs.js";
import { lakebaseClient } from "./lakebase.js";
import { databricksPipelineStartTool, databricksPipelineStatusTool } from "./lakeflow.js";
import { connectDatabricksManagedMcpServer } from "./mcp.js";
import { databricksFoundationModelProvider } from "./model.js";
import { createDatabricksNativeClients } from "./native-sdk.js";
import { createDatabricksRagChain } from "./rag-chain.js";
import type { RagTurn } from "./rag-chain.js";
import { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import {
  type DatabricksAppRecoveryEvidence,
  type DatabricksAppUserIsolationEvidence,
  type DatabricksDynamicAgentEvidence,
  validateDatabricksAppRecoveryEvidence,
  validateDatabricksAppUserIsolationEvidence,
  validateDatabricksDynamicAgentEvidence,
} from "./release-evidence.js";
import { databricksErrorMessage, databricksErrorStatusCode } from "./sdk-error.js";
import { runStatement } from "./sql-exec.js";
import { databricksTelemetry, queryDatabricksLineageEvidence } from "./telemetry-sink.js";
import { databricksTableInfoTool, unityCatalogTablesTool } from "./tools.js";
import { databricksAiSearch } from "./vector-search.js";
import { UcVolumesAttachmentStore } from "./volumes-attachment-store.js";

const env = process.env;
const host = requiredEnv("DATABRICKS_HOST");
const principal = databricksPrincipalFromEnv(env);
const identity = databricksIdentity(principal);
const client = new DatabricksRawProtocolClient({ host, token: identity });
const native = createDatabricksNativeClients({
  host,
  credentials: databricksCredentials(principal),
  tokenProvider: identity,
});
const model = env.DATABRICKS_MODEL;
const aiSearchIndex = env.DATABRICKS_AI_SEARCH_INDEX ?? env.DATABRICKS_VECTOR_INDEX;
const aiSearchTextColumn =
  env.DATABRICKS_AI_SEARCH_TEXT_COLUMN ?? env.DATABRICKS_VECTOR_TEXT_COLUMN;
const aiSearchIdColumn = env.DATABRICKS_AI_SEARCH_ID_COLUMN ?? env.DATABRICKS_VECTOR_ID_COLUMN;
const aiSearchQuery = env.DATABRICKS_AI_SEARCH_QUERY ?? env.DATABRICKS_VECTOR_QUERY;
let principalKind = env.DATABRICKS_PRINCIPAL_KIND;

const checks: DatabricksCertificationCheck[] = [
  {
    id: "identity",
    description:
      "Configured PAT or OAuth M2M identity authenticates against the current-user endpoint.",
    configured: true,
    run: async () => {
      const me = await native.scim.me({});
      assertTruthy(me.id ?? me.userName, "Current-user response did not contain an identity.");
      principalKind ??= typeof me.applicationId === "string" ? "service-principal" : "user";
      return { principalKind, principalId: String(me.id ?? me.userName) };
    },
  },
  {
    id: "ai-gateway",
    description:
      "Unity AI Gateway v2 is enabled and exposes the configured system.ai model service.",
    configured: Boolean(model?.startsWith("system.ai.")),
    run: async () => {
      await ensureDatabricksAiGateway(client);
      const services = await listDatabricksModelServices(client);
      if (!model || !services.some((service) => service.name === model)) {
        throw new Error(
          `Configured model service ${model ?? "(missing)"} was not discovered through Unity Catalog.`,
        );
      }
      return { model, discoveredServices: services.length };
    },
  },
  {
    id: "sql",
    description: "SQL Warehouse executes a statement to completion.",
    configured: Boolean(env.DATABRICKS_WAREHOUSE_ID),
    run: async () => {
      const result = (await runStatement(
        native.statements,
        requiredEnv("DATABRICKS_WAREHOUSE_ID"),
        "select 1 as fabric_harness_certification",
        [],
        { intervalMs: 1_000, timeoutMs: 120_000 },
      )) as { statementId?: string; status?: { state?: string } };
      if (!result.statementId && result.status?.state !== "SUCCEEDED")
        throw new Error("SQL statement was not admitted.");
      if (result.status?.state !== "SUCCEEDED") throw new Error("SQL statement did not succeed.");
    },
  },
  {
    id: "unity-catalog",
    description: "Acting principal can list tables in the configured UC catalog and schema.",
    configured: Boolean(env.DATABRICKS_CATALOG && env.DATABRICKS_SCHEMA),
    run: async () => {
      await unityCatalogTablesTool(native.tables).execute?.({
        catalog: requiredEnv("DATABRICKS_CATALOG"),
        schema: requiredEnv("DATABRICKS_SCHEMA"),
      });
    },
  },
  {
    id: "agent-services",
    description:
      "Unity Catalog Agent Services creates, discovers, updates, governs and deletes an external Harness agent registration.",
    configured: Boolean(
      env.DATABRICKS_AGENT_SERVICES_TEST === "1" &&
        env.DATABRICKS_AGENT_SERVICE_CONNECTION &&
        env.DATABRICKS_CATALOG &&
        env.DATABRICKS_SCHEMA,
    ),
    run: async () => {
      const catalog = requiredEnv("DATABRICKS_CATALOG");
      const schema = requiredEnv("DATABRICKS_SCHEMA");
      const serviceId = `fabric_cert_${Date.now().toString(36)}_${randomUUID().replaceAll("-", "").slice(0, 8)}`;
      const fullName = `${catalog}.${schema}.${serviceId}`;
      const services = databricksAgentServices(client, { acknowledgeBeta: true });
      let created = false;
      try {
        const service = await services.create({
          catalog,
          schema,
          id: serviceId,
          connection: requiredEnv("DATABRICKS_AGENT_SERVICE_CONNECTION"),
          basePath: env.DATABRICKS_AGENT_SERVICE_BASE_PATH ?? "/responses",
          comment: "Fabric Harness protected certification registration",
          systemPrompt: "Certification entry. Do not use for production traffic.",
        });
        created = true;
        if (!service.name?.includes(fullName)) {
          throw new Error(`Agent Service create returned an unexpected name: ${service.name}.`);
        }
        await services.get(fullName);
        await services.update(fullName, { comment: "Fabric Harness certification verified" });
        const listed = await services.list({ catalog, schema });
        if (!(listed.agent_services ?? []).some((entry) => entry.name?.includes(fullName))) {
          throw new Error(
            "Created Agent Service was not discoverable in its Unity Catalog schema.",
          );
        }
        await services.permissions(fullName);
        const testPrincipal = env.DATABRICKS_AGENT_SERVICE_TEST_PRINCIPAL;
        if (testPrincipal) {
          await services.grant(fullName, testPrincipal, ["EXECUTE", "READ_METADATA"]);
          await services.revoke(fullName, testPrincipal, ["EXECUTE", "READ_METADATA"]);
        }
        return {
          fullName,
          basePath: env.DATABRICKS_AGENT_SERVICE_BASE_PATH ?? "/responses",
          grantDifferential: Boolean(testPrincipal),
          runtimeInvocationAvailable: false,
        };
      } finally {
        if (created) await services.delete(fullName);
      }
    },
  },
  {
    id: "uc-denial",
    description: "Unity Catalog rejects SELECT against a deliberately unauthorized table.",
    configured: Boolean(env.DATABRICKS_WAREHOUSE_ID && env.DATABRICKS_DENIED_TABLE),
    run: async () => {
      try {
        const final = (await runStatement(
          native.statements,
          requiredEnv("DATABRICKS_WAREHOUSE_ID"),
          `SELECT * FROM ${sqlIdentifier(requiredEnv("DATABRICKS_DENIED_TABLE"))} LIMIT 1`,
          [],
          { intervalMs: 1_000, timeoutMs: 120_000 },
        )) as { status?: { state?: string; error?: unknown } };
        if (final?.status?.state === "SUCCEEDED") {
          throw new Error(
            "The configured denied table was readable; the UC denial control was not demonstrated.",
          );
        }
        if (/403|PERMISSION_DENIED|does not have|not authorized/i.test(JSON.stringify(final))) {
          return { deniedByUnityCatalog: true };
        }
        throw new Error(
          `Denied-table query failed for an unexpected reason: ${JSON.stringify(final)}`,
        );
      } catch (error) {
        if (/403|PERMISSION_DENIED|does not have|not authorized/i.test(String(error)))
          return { deniedByUnityCatalog: true };
        throw error;
      }
    },
  },
  {
    id: "uc-allowed-select",
    description: "Unity Catalog permits SELECT against the configured governed table.",
    configured: Boolean(env.DATABRICKS_WAREHOUSE_ID && env.DATABRICKS_ALLOWED_TABLE),
    run: async () => {
      const final = (await runStatement(
        native.statements,
        requiredEnv("DATABRICKS_WAREHOUSE_ID"),
        `SELECT * FROM ${sqlIdentifier(requiredEnv("DATABRICKS_ALLOWED_TABLE"))} LIMIT 1`,
        [],
        { intervalMs: 1_000, timeoutMs: 120_000 },
      )) as { status?: { state?: string; error?: unknown } };
      if (final.status?.state !== "SUCCEEDED") {
        throw new Error(`Allowed SELECT did not succeed: ${JSON.stringify(final.status)}`);
      }
    },
  },
  {
    id: "catalog-preflight-denial",
    description:
      "Fabric rejects a disallowed fully-qualified catalog before a REST request is sent.",
    configured: Boolean(env.DATABRICKS_CATALOG),
    run: async () => {
      let requested = false;
      const probe = {
        getTable: async () => {
          requested = true;
          return {};
        },
      } as unknown as typeof native.tables;
      const tool = withGovernance(databricksTableInfoTool(probe), {
        catalogs: [requiredEnv("DATABRICKS_CATALOG")],
      });
      const deniedObject =
        env.DATABRICKS_PREFLIGHT_DENIED_TABLE ??
        "fabric_harness_disallowed.certification.denied_probe";
      try {
        await tool.execute?.({ fullName: deniedObject });
      } catch (error) {
        if (/not in the allowed list/.test(String(error)) && !requested)
          return { deniedBeforeRest: true };
        throw error;
      }
      throw new Error("Catalog preflight did not reject the disallowed object.");
    },
  },
  {
    id: "mutation-approval",
    description: "A Lakeflow mutation is classified as approval-required for the data steward.",
    configured: Boolean(env.DATABRICKS_PIPELINE_ID),
    run: () => {
      const tool = databricksPipelineStartTool(native.pipelines, {
        allowedPipelineIds: [requiredEnv("DATABRICKS_PIPELINE_ID")],
      });
      const policy = databricksGovernancePolicy({
        tools: [tool],
        host,
        stewardAudience: "data-steward",
      });
      const decision = evaluateToolCallPolicy(
        { tool: tool.name, input: { pipelineId: requiredEnv("DATABRICKS_PIPELINE_ID") } },
        policy,
      );
      if (!decision.approvalRequired || decision.approvalAudience !== "data-steward")
        throw new Error("Mutation approval policy did not gate Lakeflow start.");
      return { approvalRequired: true, audience: decision.approvalAudience };
    },
  },
  {
    id: "model-serving",
    description: "Mosaic AI Model Serving returns a model response.",
    configured: Boolean(model),
    run: async () => {
      if (!model) throw new Error("DATABRICKS_MODEL is required.");
      const provider = databricksFoundationModelProvider({
        host,
        token: identity,
        defaultModel: model,
        requestTags: { project: "fabric-harness", purpose: "certification" },
      });
      const result = await provider.generate({
        model,
        messages: [{ role: "user", content: "Reply with the word certified." }],
      });
      assertTruthy(result.message?.content, "Model Serving returned no text.");
    },
  },
  {
    id: "rag",
    description:
      "AI Search retrieval and AI Gateway generation produce a grounded answer with a valid source citation.",
    configured: Boolean(model && aiSearchIndex && aiSearchTextColumn),
    run: async () => {
      if (!model) throw new Error("DATABRICKS_MODEL is required.");
      if (!aiSearchIndex || !aiSearchTextColumn)
        throw new Error(
          "DATABRICKS_AI_SEARCH_INDEX and DATABRICKS_AI_SEARCH_TEXT_COLUMN are required.",
        );
      const retriever = databricksAiSearch({
        client: native.vectorSearch,
        rawProtocolClient: client,
        index: aiSearchIndex,
        textColumn: aiSearchTextColumn,
        ...(aiSearchIdColumn ? { idColumn: aiSearchIdColumn } : {}),
      });
      const chain = createDatabricksRagChain({
        retriever,
        modelProvider: databricksFoundationModelProvider({
          host,
          token: identity,
          defaultModel: model,
          requestTags: { project: "fabric-harness", purpose: "rag-certification" },
        }),
        model,
        index: aiSearchIndex,
        topK: 3,
        postProcess: { requireCitations: true, citationRepairAttempts: 3 },
      });
      let turn: RagTurn | undefined;
      let streamedDeltas = 0;
      for await (const event of chain.stream({
        question: env.DATABRICKS_RAG_QUERY ?? aiSearchQuery ?? "What is Fabric Harness?",
      })) {
        if (event.type === "delta") {
          if (event.textDelta.length > 0) streamedDeltas += 1;
        } else {
          turn = event.turn;
        }
      }
      if (!turn) throw new Error("RAG certification stream returned no terminal turn.");
      if (streamedDeltas === 0)
        throw new Error("RAG certification stream returned no text deltas.");
      if (turn.chunks.length === 0) throw new Error("RAG certification retrieved no context.");
      if (turn.citations.length === 0)
        throw new Error("RAG certification answer contained no valid inline citation.");
      return {
        retrieved: turn.chunks.length,
        citations: turn.citations.map((citation) => citation.id),
        streaming: true,
        streamedDeltas,
      };
    },
  },
  {
    id: "responses-agent",
    description:
      "The registered MLflow ResponsesAgent accepts the Responses API schema, streams stable output items, and securely reaches the Fabric App.",
    configured: Boolean(env.DATABRICKS_RESPONSES_AGENT_ENDPOINT),
    run: async () => {
      const endpoint = requiredEnv("DATABRICKS_RESPONSES_AGENT_ENDPOINT");
      const endpointState = await native.modelServing.getInferenceEndpoint({ name: endpoint });
      if (!endpointState.aiGateway?.inferenceTableConfig?.enabled) {
        throw new Error(
          "ResponsesAgent serving endpoint does not have AI Gateway inference tables enabled.",
        );
      }
      const request = {
        input: [{ role: "user", content: "Reply with certified." }],
        context: { conversation_id: `cert-${randomUUID()}` },
        custom_inputs: {
          request_id: `cert-${randomUUID()}`,
        },
        databricks_options: { return_trace: true },
      };
      const result = await client.post<Record<string, unknown>>(
        `/serving-endpoints/${encodeURIComponent(endpoint)}/invocations`,
        request,
      );
      const output = Array.isArray(result.output) ? result.output : [];
      if (output.length === 0) throw new Error("ResponsesAgent proxy returned no output items.");
      const streamResponse = await client.postRaw(
        `/serving-endpoints/${encodeURIComponent(endpoint)}/invocations`,
        {
          ...request,
          custom_inputs: { request_id: `cert-stream-${randomUUID()}` },
          stream: true,
        },
        {},
        { retry: "never", accept: "text/event-stream" },
      );
      if (!streamResponse.ok) {
        throw new Error(`ResponsesAgent streaming invocation returned ${streamResponse.status}.`);
      }
      const streamText = await streamResponse.text();
      const chunks = streamText
        .split(/\r?\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .filter((line) => line && line !== "[DONE]")
        .map(
          (line) =>
            JSON.parse(line) as {
              type?: unknown;
              item_id?: unknown;
              delta?: unknown;
              item?: { id?: unknown };
            },
        );
      if (
        chunks.length === 0 ||
        !chunks.some(
          (chunk) => chunk.type === "response.output_text.delta" && typeof chunk.delta === "string",
        )
      ) {
        throw new Error("ResponsesAgent streaming invocation returned no text deltas.");
      }
      const itemIds = new Set(
        chunks
          .flatMap((chunk) => [chunk.item_id, chunk.item?.id])
          .filter((id): id is string => typeof id === "string"),
      );
      if (itemIds.size !== 1)
        throw new Error("ResponsesAgent stream did not preserve one output item id.");
      if (!chunks.some((chunk) => chunk.type === "response.output_item.done")) {
        throw new Error("ResponsesAgent stream did not include response.output_item.done.");
      }
      return {
        endpoint,
        inferenceTables: true,
        streaming: true,
        outputItems: output.length,
        events: chunks.length,
      };
    },
  },
  {
    id: "rag-evaluation",
    description:
      "Databricks MLflow 3 evaluates the governed RAG golden set with managed relevance, groundedness, sufficiency, and correctness judges.",
    configured: Boolean(env.DATABRICKS_RAG_EVAL_JOB_ID),
    run: async () => {
      const jobs = databricksJobs(native.jobs);
      const receipt = await jobs.runJob({
        jobId: Number(requiredEnv("DATABRICKS_RAG_EVAL_JOB_ID")),
        idempotencyToken: `fabric-rag-eval-${randomUUID()}`,
      });
      const state = await jobs.wait(receipt.runId, {
        timeoutMs: 3_600_000,
        pollIntervalMs: 10_000,
      });
      if (state.resultState !== "SUCCESS")
        throw new Error(
          `MLflow RAG evaluation job ended in ${state.resultState ?? state.lifeCycleState}.`,
        );
      const outputs = await jobs.getOutputs(receipt.runId);
      return { runId: receipt.runId, resultState: state.resultState, outputCount: outputs.length };
    },
  },
  {
    id: "ai-search",
    description: "Databricks AI Search returns governed index results.",
    configured: Boolean(aiSearchIndex && aiSearchTextColumn),
    run: async () => {
      if (!aiSearchIndex || !aiSearchTextColumn)
        throw new Error(
          "DATABRICKS_AI_SEARCH_INDEX and DATABRICKS_AI_SEARCH_TEXT_COLUMN are required.",
        );
      const results = await databricksAiSearch({
        client: native.vectorSearch,
        rawProtocolClient: client,
        index: aiSearchIndex,
        textColumn: aiSearchTextColumn,
      }).retrieve(aiSearchQuery ?? "fabric harness certification", { k: 1 });
      if (!Array.isArray(results)) throw new Error("AI Search returned an invalid result.");
    },
  },
  {
    id: "genie",
    description: "AI/BI Genie completes a natural-language analytics request.",
    configured: Boolean(env.DATABRICKS_GENIE_SPACE_ID),
    run: async () => {
      const result = (await databricksGenieTool(native.genie, {
        agentId: requiredEnv("DATABRICKS_GENIE_SPACE_ID"),
        rawProtocolClient: client,
        timeoutMs: 180_000,
      }).execute?.({
        question: env.DATABRICKS_GENIE_QUESTION ?? "How many rows are available?",
      })) as { status?: string };
      if (result?.status !== "COMPLETED")
        throw new Error(`Genie ended in status ${result?.status ?? "unknown"}.`);
    },
  },
  {
    id: "managed-mcp",
    description:
      "A Databricks managed MCP read tool is discovered and invoked under the configured OBO identity without token disclosure.",
    configured: Boolean(
      env.DATABRICKS_OBO_TOKEN && env.DATABRICKS_MANAGED_MCP_URL && env.DATABRICKS_MANAGED_MCP_TOOL,
    ),
    run: async () => {
      const remoteTool = requiredEnv("DATABRICKS_MANAGED_MCP_TOOL");
      const connection = await connectDatabricksManagedMcpServer({
        host,
        tokenProvider: async () => requiredEnv("DATABRICKS_OBO_TOKEN"),
        server: {
          name: "certification",
          endpoint: { kind: "url", url: requiredEnv("DATABRICKS_MANAGED_MCP_URL") },
          allowTools: [remoteTool],
          effects: { [remoteTool]: "read" },
        },
      });
      try {
        const tool = connection.tools.find((candidate) => {
          const metadata = candidate.metadata as { mcp?: { tool?: unknown } } | undefined;
          return metadata?.mcp?.tool === remoteTool;
        });
        if (!tool?.execute)
          throw new Error(`Managed MCP did not expose required tool ${remoteTool}.`);
        const input = optionalJson<JsonObject>("DATABRICKS_MANAGED_MCP_INPUT_JSON") ?? {};
        const signal = AbortSignal.timeout(
          Number(env.DATABRICKS_MANAGED_MCP_TIMEOUT_MS ?? 180_000),
        );
        const output = await tool.execute(input, { sandbox: new EmptySandboxEnv(), signal });
        const token = requiredEnv("DATABRICKS_OBO_TOKEN");
        if (String(output).includes(token))
          throw new Error("Managed MCP output disclosed the OBO bearer token.");
        return {
          tool: remoteTool,
          effect: tool.metadata?.effect,
          outputObserved: output !== undefined,
          authMode: "on-behalf-of",
        };
      } finally {
        await connection.close();
      }
    },
  },
  {
    id: "genie-agent-mode",
    description: "Beta Genie Agent Mode produces ordered output and a terminal SSE event.",
    configured: Boolean(env.DATABRICKS_GENIE_AGENT_MODE_ID),
    run: async () => {
      const transcript: Array<{ type: string; sequenceNumber: number }> = [];
      let outputItems = 0;
      let terminal = false;
      const agentMode = databricksGenieAgentMode(client, {
        agentId: requiredEnv("DATABRICKS_GENIE_AGENT_MODE_ID"),
        acknowledgeBeta: true,
        timeoutMs: Number(env.DATABRICKS_GENIE_AGENT_MODE_TIMEOUT_MS ?? 5 * 60_000),
        onEvent: (event) => {
          transcript.push({ type: event.type, sequenceNumber: event.sequenceNumber });
          if (event.type.startsWith("response.output_item.")) outputItems += 1;
          if (event.type === "response.completed") terminal = true;
        },
      });
      await agentMode.complete(
        env.DATABRICKS_GENIE_AGENT_MODE_QUESTION ?? "How many rows are available?",
      );
      if (!terminal || outputItems === 0)
        throw new Error("Agent Mode did not emit both an output item and a terminal event.");
      for (let index = 1; index < transcript.length; index += 1) {
        const current = transcript[index];
        const previous = transcript[index - 1];
        if (!current || !previous || current.sequenceNumber <= previous.sequenceNumber)
          throw new Error("Agent Mode sequence numbers were not strictly increasing.");
      }
      return { transcript, outputItems, terminal };
    },
  },
  {
    id: "feature-serving",
    description: "Feature Serving resolves configured primary-key records.",
    configured: Boolean(env.DATABRICKS_FEATURE_ENDPOINT && env.DATABRICKS_FEATURE_RECORDS_JSON),
    run: async () => {
      const records = JSON.parse(requiredEnv("DATABRICKS_FEATURE_RECORDS_JSON")) as JsonObject[];
      assertTruthy(
        await databricksFeatureLookupTool(native.modelServingQuery, {
          endpoint: requiredEnv("DATABRICKS_FEATURE_ENDPOINT"),
        }).execute?.({ records }),
        "Feature Serving returned no result.",
      );
    },
  },
  {
    id: "lakeflow",
    description: "Lakeflow pipeline status is readable under the acting principal.",
    configured: Boolean(env.DATABRICKS_PIPELINE_ID),
    run: async () => {
      assertTruthy(
        await databricksPipelineStatusTool(native.pipelines).execute?.({
          pipelineId: requiredEnv("DATABRICKS_PIPELINE_ID"),
        }),
        "Lakeflow returned no pipeline.",
      );
    },
  },
  {
    id: "jobs",
    description:
      "Jobs run-now accepts a stable idempotency token, terminates successfully, and exposes output.",
    configured: Boolean(env.DATABRICKS_JOB_ID),
    run: async () => {
      const jobs = databricksJobs(native.jobs);
      const receipt = await jobs.runJob({
        jobId: Number(requiredEnv("DATABRICKS_JOB_ID")),
        idempotencyToken: `fabric-cert-${randomUUID()}`,
      });
      const state = await jobs.wait(receipt.runId, { timeoutMs: 900_000, pollIntervalMs: 5_000 });
      if (state.resultState !== "SUCCESS")
        throw new Error(`Jobs run ended in ${state.resultState ?? state.lifeCycleState}.`);
      const outputs = await jobs.getOutputs(receipt.runId);
      return { runId: receipt.runId, resultState: state.resultState, outputCount: outputs.length };
    },
  },
  {
    id: "notebook",
    description: "A one-off notebook task executes on the configured existing cluster.",
    configured: Boolean(env.DATABRICKS_NOTEBOOK_PATH),
    run: async () => {
      const jobs = databricksJobs(native.jobs);
      const receipt = await jobs.submitNotebook({
        notebookPath: requiredEnv("DATABRICKS_NOTEBOOK_PATH"),
        ...(env.DATABRICKS_CLUSTER_ID ? { existingClusterId: env.DATABRICKS_CLUSTER_ID } : {}),
        idempotencyToken: `fabric-cert-notebook-${randomUUID()}`,
      });
      const state = await jobs.wait(receipt.runId, { timeoutMs: 900_000, pollIntervalMs: 5_000 });
      if (state.resultState !== "SUCCESS")
        throw new Error(`Notebook run ended in ${state.resultState ?? state.lifeCycleState}.`);
      return { runId: receipt.runId, resultState: state.resultState };
    },
  },
  {
    id: "volumes",
    description: "UC Volumes supports attachment write, read, and delete.",
    configured: Boolean(env.DATABRICKS_CATALOG && env.DATABRICKS_SCHEMA && env.DATABRICKS_VOLUME),
    run: async () => {
      const bytes = new TextEncoder().encode(`fabric-harness-certification:${randomUUID()}`);
      const digest = createHash("sha256").update(bytes).digest("hex");
      const scope = `certification-${randomUUID()}`;
      const ref: AttachmentRef = {
        id: randomUUID(),
        digest,
        mimeType: "text/plain",
        sizeBytes: bytes.byteLength,
        filename: "certification.txt",
      };
      const store = new UcVolumesAttachmentStore({
        client: native.files,
        catalog: requiredEnv("DATABRICKS_CATALOG"),
        schema: requiredEnv("DATABRICKS_SCHEMA"),
        volume: requiredEnv("DATABRICKS_VOLUME"),
        rootPrefix: env.DATABRICKS_VOLUME_ROOT ?? "fabric-harness-certification",
      });
      await store.put({ scope, ref, bytes });
      const loaded = await store.get(scope, digest);
      if (!loaded || loaded.ref.digest !== digest)
        throw new Error("UC Volumes attachment round trip failed.");
      await store.delete(scope, digest);
    },
  },
  {
    id: "lakebase",
    description: "Lakebase credential exchange opens a managed Postgres connection.",
    configured: Boolean(env.ENDPOINT_NAME && env.PGHOST && env.PGDATABASE && env.PGUSER),
    run: async () => {
      const pool = lakebaseClient({
        endpoint: requiredEnv("ENDPOINT_NAME"),
        credentialClient: native.postgres,
        host: requiredEnv("PGHOST"),
        database: requiredEnv("PGDATABASE"),
        user: requiredEnv("PGUSER"),
        port: Number(env.PGPORT ?? 5432),
        ssl: { rejectUnauthorized: true },
        max: 1,
      });
      try {
        const result = await pool.query("select current_user, current_database()");
        assertTruthy(result.rows[0], "Lakebase returned no row.");
      } finally {
        await pool.end();
      }
    },
  },
  {
    id: "lineage",
    description: "Lakebase joins a governed UC object to submission, actor, outcome, and cost.",
    configured: Boolean(
      env.ENDPOINT_NAME &&
        env.PGHOST &&
        env.PGDATABASE &&
        env.PGUSER &&
        env.DATABRICKS_ALLOWED_TABLE,
    ),
    run: async () => {
      const pool = lakebaseClient({
        endpoint: requiredEnv("ENDPOINT_NAME"),
        credentialClient: native.postgres,
        host: requiredEnv("PGHOST"),
        database: requiredEnv("PGDATABASE"),
        user: requiredEnv("PGUSER"),
        port: Number(env.PGPORT ?? 5432),
        ssl: { rejectUnauthorized: true },
        max: 1,
      });
      const submissionId = `cert-lineage-${randomUUID()}`;
      try {
        const telemetry = databricksTelemetry({ client: pool });
        await telemetry.migrate();
        await telemetry.sink.record({
          type: "submission_usage",
          submissionId,
          kind: "certification",
          agent: "databricks-certifier",
          instanceId: "partner-review",
          session: "default",
          tenantId: "certification",
          at: Date.now(),
          model: model ?? "certification",
          costUsd: 0.01,
          actor: { principal: { kind: "app-principal", id: principalKind ?? "databricks-app" } },
        });
        const tool = withGovernance(databricksTableInfoTool(native.tables), {
          principal: principalKind ?? "databricks-app",
          catalogs: [requiredEnv("DATABRICKS_CATALOG")],
          onLineage: telemetry.onLineage,
        });
        await runWithSubmissionContext(
          {
            submissionId,
            attemptId: "attempt-1",
            agent: "databricks-certifier",
            instanceId: "partner-review",
            session: "default",
            tenantId: "certification",
            actor: { principal: { kind: "app-principal", id: principalKind ?? "databricks-app" } },
          },
          () => tool.execute?.({ fullName: requiredEnv("DATABRICKS_ALLOWED_TABLE") }),
        );
        let rows = [] as Awaited<ReturnType<typeof queryDatabricksLineageEvidence>>;
        for (let attempt = 0; attempt < 20 && rows.length === 0; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          rows = await queryDatabricksLineageEvidence(pool, submissionId);
        }
        const row = rows[0];
        if (
          !row?.actor ||
          row.object !== requiredEnv("DATABRICKS_ALLOWED_TABLE") ||
          row.outcome !== "succeeded" ||
          row.estimatedCostUsd < 0.01
        ) {
          throw new Error("Lineage evidence did not join actor, object, outcome, and cost.");
        }
        return {
          submissionId,
          actor: row.actor,
          object: row.object,
          outcome: row.outcome,
          estimatedCostUsd: row.estimatedCostUsd,
        };
      } finally {
        await pool
          .query("DELETE FROM fh_lineage WHERE submission_id = $1", [submissionId])
          .catch(() => undefined);
        await pool
          .query("DELETE FROM fh_submission_telemetry WHERE submission_id = $1", [submissionId])
          .catch(() => undefined);
        await pool.end();
      }
    },
  },
  {
    id: "obo",
    description:
      "A forwarded user token resolves identity and demonstrates a different UC grant outcome from the App principal.",
    configured: Boolean(env.DATABRICKS_OBO_TOKEN && env.DATABRICKS_OBO_DIFFERENTIAL_TABLE),
    run: async () => {
      const oboIdentity = async () => requiredEnv("DATABRICKS_OBO_TOKEN");
      const obo = createDatabricksNativeClients({
        host,
        credentials: databricksCredentialsFromTokenProvider(oboIdentity, "certification-obo"),
        tokenProvider: oboIdentity,
      });
      const me = await obo.scim.me({});
      assertTruthy(me.id ?? me.userName, "OBO token did not resolve a user identity.");
      const table = requiredEnv("DATABRICKS_OBO_DIFFERENTIAL_TABLE");
      const [appAllowed, userAllowed] = await Promise.all([
        canReadTable(native.tables, table),
        canReadTable(obo.tables, table),
      ]);
      const expected = env.DATABRICKS_OBO_EXPECT ?? "user-allowed";
      if (appAllowed === userAllowed)
        throw new Error(
          "App and OBO principals produced the same UC grant outcome; configure a differential table.",
        );
      if (expected === "user-allowed" && !userAllowed)
        throw new Error("Expected the OBO user to read the differential table.");
      if (expected === "app-allowed" && !appAllowed)
        throw new Error("Expected the App principal to read the differential table.");
      return {
        appAllowed,
        userAllowed,
        expected,
        credential: {
          acquisitionMode: env.FABRIC_DATABRICKS_OBO_ACQUISITION_MODE ?? "unspecified",
          remainingSecondsAtCheck: jwtRemainingSeconds(requiredEnv("DATABRICKS_OBO_TOKEN")),
        },
      };
    },
  },
  {
    id: "system-tables",
    description: "System billing tables return attributed usage through SQL Warehouse.",
    configured: Boolean(env.DATABRICKS_WAREHOUSE_ID && env.DATABRICKS_SYSTEM_TABLES_TEST === "1"),
    run: async () => {
      const summary = await databricksConsumption(native.statements, {
        warehouseId: requiredEnv("DATABRICKS_WAREHOUSE_ID"),
      }).summary({
        startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
        groupBy: "sku",
      });
      if (!Array.isArray(summary.rows))
        throw new Error("System Tables returned an invalid summary.");
    },
  },
  {
    id: "actual-cost-reconciliation",
    description:
      "Delayed System Tables actuals reconcile with an estimate and exercise drift alerting.",
    configured: Boolean(
      env.DATABRICKS_WAREHOUSE_ID &&
        env.DATABRICKS_SYSTEM_TABLES_TEST === "1" &&
        env.DATABRICKS_COST_SCOPE_KEY &&
        env.DATABRICKS_ESTIMATED_COST_USD,
    ),
    run: async () => {
      let alerted = false;
      const result = await reconcileDatabricksCost({
        scopeKey: requiredEnv("DATABRICKS_COST_SCOPE_KEY"),
        estimatedUsd: Number(requiredEnv("DATABRICKS_ESTIMATED_COST_USD")),
        usageOccurredAt: env.DATABRICKS_COST_OCCURRED_AT ?? new Date(Date.now() - 48 * 60 * 60_000),
        accountingDelayMs: Number(env.DATABRICKS_ACCOUNTING_DELAY_MS ?? 24 * 60 * 60_000),
        actualSource: databricksActualCostSource(
          native.statements,
          requiredEnv("DATABRICKS_WAREHOUSE_ID"),
          {
            cacheTtlMs: 0,
          },
        ),
        maxDriftUsd: Number(env.DATABRICKS_MAX_COST_DRIFT_USD ?? 1),
        maxDriftPercent: Number(env.DATABRICKS_MAX_COST_DRIFT_PERCENT ?? 10),
        unavailable: "fail-closed",
        onDrift: () => {
          alerted = true;
        },
      });
      if (result.status === "pending-accounting")
        throw new Error("Configured usage has not passed the accounting delay.");
      if (result.status === "drift" && !alerted)
        throw new Error("Cost drift did not invoke the alert hook.");
      return { ...result, alerted };
    },
  },
  {
    id: "databricks-app",
    description: "The deployed Databricks App passes its health endpoint.",
    configured: Boolean(env.DATABRICKS_APP_URL),
    run: async () => {
      const response = await fetch(`${env.DATABRICKS_APP_URL?.replace(/\/$/, "")}/health`, {
        headers: { authorization: `Bearer ${await identity()}` },
      });
      if (!response.ok) throw new Error(`Databricks App health returned HTTP ${response.status}.`);
      const body = (await response.json()) as { ok?: boolean };
      if (body.ok !== true) throw new Error("Databricks App health response was not ok.");
    },
  },
  {
    id: "lakebase-app-restart",
    description:
      "The deployed App preserves Lakebase-backed state and approval identity across stop/start, then cascades deletion.",
    configured: Boolean(env.FABRIC_DATABRICKS_APP_RECOVERY_EVIDENCE_PATH),
    run: async () => {
      const recovery = JSON.parse(
        await readFile(
          path.resolve(requiredEnv("FABRIC_DATABRICKS_APP_RECOVERY_EVIDENCE_PATH")),
          "utf8",
        ),
      ) as DatabricksAppRecoveryEvidence;
      validateDatabricksAppRecoveryEvidence(recovery);
      return {
        preparedAt: recovery.preparedAt,
        verifiedAt: recovery.verifiedAt,
        cascadeDeletion: recovery.cascadeDeletion,
      };
    },
  },
  {
    id: "dynamic-agent",
    description:
      "The deployed App exercises hook rendering, durable state, conditional resources, and immutable instance data across restart.",
    configured: Boolean(env.FABRIC_DATABRICKS_DYNAMIC_EVIDENCE_PATH),
    run: async () => {
      const dynamic = JSON.parse(
        await readFile(
          path.resolve(requiredEnv("FABRIC_DATABRICKS_DYNAMIC_EVIDENCE_PATH")),
          "utf8",
        ),
      ) as DatabricksDynamicAgentEvidence;
      validateDatabricksDynamicAgentEvidence(dynamic);
      return { preparedAt: dynamic.preparedAt, verifiedAt: dynamic.verifiedAt };
    },
  },
  ...createDatabricksAuthoringCertificationChecks(authoringFixtures()),
];

const required = splitCsv(env.FABRIC_DATABRICKS_REQUIRED_CHECKS ?? "");
const requiredTiers = splitCsv(
  env.FABRIC_DATABRICKS_REQUIRED_TIERS ??
    (env.FABRIC_DATABRICKS_AUTHORING_TEST === "1" ? "A" : "R"),
);
const invalidRequiredTiers = requiredTiers.filter((tier) => tier !== "R" && tier !== "A");
if (invalidRequiredTiers.length > 0) {
  throw new Error(
    `FABRIC_DATABRICKS_REQUIRED_TIERS accepts only R and A; received ${invalidRequiredTiers.join(", ")}.`,
  );
}
const packageManifest = JSON.parse(
  await readFile(new URL("../package.json", import.meta.url), "utf8"),
) as { version?: string };
const artifactSha256 = env.FABRIC_DATABRICKS_ARTIFACT_PATH
  ? await digestDatabricksArtifact(path.resolve(env.FABRIC_DATABRICKS_ARTIFACT_PATH))
  : undefined;
const packageArtifactSha256 = env.FABRIC_DATABRICKS_PACKAGE_ARTIFACT_PATH
  ? await digestDatabricksArtifact(path.resolve(env.FABRIC_DATABRICKS_PACKAGE_ARTIFACT_PATH))
  : undefined;
const relatedEvidenceSha256: Record<string, string> = {};
if (env.FABRIC_DATABRICKS_APP_RECOVERY_EVIDENCE_PATH) {
  const recoveryPath = path.resolve(env.FABRIC_DATABRICKS_APP_RECOVERY_EVIDENCE_PATH);
  const recovery = JSON.parse(
    await readFile(recoveryPath, "utf8"),
  ) as DatabricksAppRecoveryEvidence;
  validateDatabricksAppRecoveryEvidence(recovery);
  relatedEvidenceSha256["app-recovery"] = await digestDatabricksArtifact(recoveryPath);
}
if (env.FABRIC_DATABRICKS_USER_ISOLATION_EVIDENCE_PATH) {
  const isolationPath = path.resolve(env.FABRIC_DATABRICKS_USER_ISOLATION_EVIDENCE_PATH);
  const isolation = JSON.parse(
    await readFile(isolationPath, "utf8"),
  ) as DatabricksAppUserIsolationEvidence;
  validateDatabricksAppUserIsolationEvidence(isolation);
  relatedEvidenceSha256["app-user-isolation"] = await digestDatabricksArtifact(isolationPath);
}
if (env.FABRIC_DATABRICKS_DYNAMIC_EVIDENCE_PATH) {
  const dynamicPath = path.resolve(env.FABRIC_DATABRICKS_DYNAMIC_EVIDENCE_PATH);
  const dynamic = JSON.parse(
    await readFile(dynamicPath, "utf8"),
  ) as DatabricksDynamicAgentEvidence;
  validateDatabricksDynamicAgentEvidence(dynamic);
  relatedEvidenceSha256["dynamic-agent"] = await digestDatabricksArtifact(dynamicPath);
}
const evidence = await runDatabricksCertification({
  workspaceHost: host,
  checks,
  required,
  requiredTiers: requiredTiers as DatabricksCertificationBlockingTier[],
  ...(env.GITHUB_SHA ? { commitSha: env.GITHUB_SHA } : {}),
  secrets: [
    principal.kind === "pat"
      ? principal.token
      : principal.kind === "service-principal"
        ? principal.clientSecret
        : "",
    env.DATABRICKS_OBO_TOKEN ?? "",
    env.DATABRICKS_AUTHORING_SECRET_VALUE ?? "",
  ].filter(Boolean),
  ...(env.DATABRICKS_WORKSPACE_CLOUD ? { workspaceCloud: env.DATABRICKS_WORKSPACE_CLOUD } : {}),
  ...(env.DATABRICKS_WORKSPACE_REGION ? { workspaceRegion: env.DATABRICKS_WORKSPACE_REGION } : {}),
  ...(env.DATABRICKS_WORKSPACE_ID ? { workspaceId: env.DATABRICKS_WORKSPACE_ID } : {}),
  runtimeVersion: `node ${process.version}`,
  ...(packageManifest.version ? { packageVersion: packageManifest.version } : {}),
  ...(principalKind ? { principalKind } : {}),
  authMode:
    env.DATABRICKS_AUTH_MODE ??
    (principalKind === "service-principal"
      ? "oauth-m2m"
      : principalKind === "cli-profile"
        ? "cli"
        : "pat"),
  apiVersions: { ...DATABRICKS_API_VERSIONS },
  ...(packageArtifactSha256 ? { packageArtifactSha256 } : {}),
  ...(artifactSha256 ? { artifactSha256 } : {}),
  ...(Object.keys(relatedEvidenceSha256).length > 0 ? { relatedEvidenceSha256 } : {}),
});
const output = path.resolve(
  env.FABRIC_DATABRICKS_EVIDENCE_PATH ?? "artifacts/databricks-certification.json",
);
await mkdir(path.dirname(output), { recursive: true });
await writeFile(output, `${JSON.stringify(evidence, null, 2)}\n`);
if (
  evidence.passed &&
  evidence.workspaceCloud &&
  evidence.workspaceRegion &&
  evidence.authMode &&
  evidence.runtimeVersion &&
  evidence.packageVersion &&
  evidence.artifactSha256
) {
  const compatibilityOutput = path.join(path.dirname(output), "databricks-compatibility.json");
  await writeFile(
    compatibilityOutput,
    `${JSON.stringify(databricksCompatibilityRecord(evidence), null, 2)}\n`,
  );
}
process.stdout.write(`${JSON.stringify(evidence, null, 2)}\n`);
if (!evidence.passed) process.exitCode = 1;

function authoringFixtures(): DatabricksAuthoringCertificationFixtures {
  const enabled = env.FABRIC_DATABRICKS_AUTHORING_TEST === "1";
  const principalKind = env.DATABRICKS_AUTHORING_EXECUTING_PRINCIPAL_KIND ?? "user";
  if (
    enabled &&
    principalKind !== "user" &&
    principalKind !== "service-principal" &&
    principalKind !== "app-principal"
  ) {
    throw new Error(
      "DATABRICKS_AUTHORING_EXECUTING_PRINCIPAL_KIND must be user, service-principal, or app-principal.",
    );
  }
  const executingPrincipal: FabricPrincipal = enabled
    ? {
        kind: principalKind as FabricPrincipal["kind"],
        id: requiredEnv("DATABRICKS_AUTHORING_EXECUTING_PRINCIPAL_ID"),
        ...(env.DATABRICKS_AUTHORING_EXECUTING_UC_PRINCIPAL
          ? { ucPrincipal: env.DATABRICKS_AUTHORING_EXECUTING_UC_PRINCIPAL }
          : {}),
      }
    : { kind: "service-principal", id: "authoring-certification-disabled" };
  const suffix = `${Date.now().toString(36)}-${randomUUID().slice(0, 8)}`;
  const jobSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["jobs"]>["spec"]
  >("DATABRICKS_AUTHORING_JOB_SPEC");
  const classicJobSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["classicJobs"]>["spec"]
  >("DATABRICKS_AUTHORING_CLASSIC_JOB_SPEC");
  const lakeflowSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["lakeflow"]>["spec"]
  >("DATABRICKS_AUTHORING_LAKEFLOW_SPEC");
  const searchFixture = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["aiSearch"]>
  >("DATABRICKS_AUTHORING_AI_SEARCH_SPEC");
  const deltaSearchFixture = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["deltaAiSearch"]>
  >("DATABRICKS_AUTHORING_DELTA_SYNC_SPEC");
  const servingSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["serving"]>["spec"]
  >("DATABRICKS_AUTHORING_SERVING_SPEC");
  const provisionedServingSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["provisionedServing"]>["spec"]
  >("DATABRICKS_AUTHORING_PROVISIONED_THROUGHPUT_SPEC");
  const genieSpec = optionalJson<
    NonNullable<DatabricksAuthoringCertificationFixtures["genie"]>["spec"]
  >("DATABRICKS_AUTHORING_GENIE_SPEC");
  const authMode =
    env.DATABRICKS_AUTHORING_AUTH_MODE ?? (env.DATABRICKS_OBO_TOKEN ? "on-behalf-of" : undefined);
  const secretValue = env.DATABRICKS_AUTHORING_SECRET_VALUE;
  const secretName = env.DATABRICKS_AUTHORING_SECRET_NAME ?? "DATABRICKS_AUTHORING_SECRET_VALUE";
  const authoringIdentity =
    enabled && env.DATABRICKS_OBO_TOKEN ? async () => env.DATABRICKS_OBO_TOKEN as string : identity;
  const authoringNative = createDatabricksNativeClients({
    host,
    credentials:
      enabled && env.DATABRICKS_OBO_TOKEN
        ? databricksCredentialsFromTokenProvider(authoringIdentity, executingPrincipal.id)
        : databricksCredentials(principal),
    tokenProvider: authoringIdentity,
  });

  return {
    enabled,
    host,
    client:
      enabled && env.DATABRICKS_OBO_TOKEN
        ? new DatabricksRawProtocolClient({ host, token: env.DATABRICKS_OBO_TOKEN })
        : client,
    jobsClient: authoringNative.jobs,
    pipelinesClient: authoringNative.pipelines,
    statementsClient: authoringNative.statements,
    vectorSearchClient: authoringNative.vectorSearch,
    modelServingClient: authoringNative.modelServing,
    unityCatalogClients: {
      catalogs: authoringNative.catalogs,
      schemas: authoringNative.schemas,
      volumes: authoringNative.volumes,
      grants: authoringNative.grants,
    },
    secretsClient: authoringNative.secrets,
    scimClient: authoringNative.scim,
    genieClient: authoringNative.genie,
    accessManagementClient: authoringNative.accessManagement,
    executingPrincipal,
    ...(authMode ? { authMode } : {}),
    steward: {
      id: enabled ? requiredEnv("DATABRICKS_AUTHORING_STEWARD_ID") : "disabled",
    },
    ...(jobSpec
      ? {
          jobs: {
            spec: { ...jobSpec, name: databricksCertificationResourceName(jobSpec.name, suffix) },
            computePolicy:
              optionalJson<
                NonNullable<DatabricksAuthoringCertificationFixtures["jobs"]>["computePolicy"]
              >("DATABRICKS_AUTHORING_COMPUTE_POLICY") ?? {},
          },
        }
      : {}),
    ...(classicJobSpec
      ? {
          classicJobs: {
            spec: {
              ...classicJobSpec,
              name: databricksCertificationResourceName(classicJobSpec.name, suffix),
            },
            computePolicy:
              optionalJson<
                NonNullable<
                  DatabricksAuthoringCertificationFixtures["classicJobs"]
                >["computePolicy"]
              >("DATABRICKS_AUTHORING_COMPUTE_POLICY") ?? {},
          },
        }
      : {}),
    ...(lakeflowSpec
      ? {
          lakeflow: {
            spec: {
              ...lakeflowSpec,
              name: databricksCertificationResourceName(lakeflowSpec.name, suffix),
            },
          },
        }
      : {}),
    ...(searchFixture
      ? {
          aiSearch: {
            endpointName: databricksCertificationResourceName(
              searchFixture.endpointName,
              suffix,
              49,
            ),
            index: {
              ...searchFixture.index,
              name: qualifiedCertificationName(searchFixture.index.name, suffix),
              endpointName: databricksCertificationResourceName(
                searchFixture.endpointName,
                suffix,
                49,
              ),
            },
          },
        }
      : {}),
    ...(deltaSearchFixture
      ? {
          deltaAiSearch: {
            endpointName: databricksCertificationResourceName(
              deltaSearchFixture.endpointName,
              suffix,
              49,
            ),
            index: {
              ...deltaSearchFixture.index,
              name: qualifiedCertificationName(deltaSearchFixture.index.name, suffix),
              endpointName: databricksCertificationResourceName(
                deltaSearchFixture.endpointName,
                suffix,
                49,
              ),
            },
          },
        }
      : {}),
    ...(servingSpec
      ? {
          serving: {
            spec: {
              ...servingSpec,
              name: databricksCertificationResourceName(servingSpec.name, suffix),
            },
          },
        }
      : {}),
    ...(provisionedServingSpec
      ? {
          provisionedServing: {
            spec: {
              ...provisionedServingSpec,
              name: databricksCertificationResourceName(provisionedServingSpec.name, suffix),
            },
          },
        }
      : {}),
    ...(genieSpec
      ? {
          genie: {
            spec: {
              ...genieSpec,
              title: databricksCertificationResourceName(genieSpec.title, suffix, 80),
            },
            question: env.DATABRICKS_AUTHORING_GENIE_QUESTION ?? "How many rows are available?",
          },
        }
      : {}),
    ...(env.DATABRICKS_AUTHORING_UC_CATALOG && env.DATABRICKS_AUTHORING_UC_SCHEMA_PREFIX
      ? {
          unityCatalog: {
            catalog: env.DATABRICKS_AUTHORING_UC_CATALOG,
            schemaName: databricksCertificationResourceName(
              env.DATABRICKS_AUTHORING_UC_SCHEMA_PREFIX,
              suffix,
            ).replaceAll("-", "_"),
            oboPrincipal: requiredEnv("DATABRICKS_AUTHORING_EXECUTING_UC_PRINCIPAL"),
          },
        }
      : {}),
    ...(env.DATABRICKS_AUTHORING_WORKSPACE_ROOT
      ? {
          workspace: {
            rootPath: `${env.DATABRICKS_AUTHORING_WORKSPACE_ROOT.replace(/\/$/, "")}/${databricksCertificationResourceName("workspace", suffix)}`,
          },
        }
      : {}),
    ...(env.DATABRICKS_AUTHORING_SECRET_SCOPE_PREFIX && secretValue
      ? {
          secrets: {
            scope: databricksCertificationResourceName(
              env.DATABRICKS_AUTHORING_SECRET_SCOPE_PREFIX,
              suffix,
            ),
            key: "probe",
            secretName,
            provider: {
              name: "databricks-authoring-certification-environment",
              resolve: (reference) => (reference.name === secretName ? secretValue : undefined),
            },
          },
        }
      : {}),
  };
}

function optionalJson<T>(name: string): T | undefined {
  const value = env[name];
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    throw new Error(
      `${name} must contain valid JSON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

function qualifiedCertificationName(name: string, suffix: string): string {
  const parts = name.split(".");
  if (parts.length !== 3) {
    throw new Error("DATABRICKS_AUTHORING_AI_SEARCH_SPEC index.name must be three-part UC name.");
  }
  parts[2] = databricksCertificationResourceName(parts[2] ?? "index", suffix).replaceAll("-", "_");
  return parts.join(".");
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function requiredEnv(name: string): string {
  const value = env[name];
  if (!value) throw new Error(`${name} is required for Databricks certification.`);
  return value;
}

function jwtRemainingSeconds(token: string, nowMs = Date.now()): number {
  const payload = token.split(".")[1];
  if (!payload) throw new Error("Databricks OBO token does not contain JWT claims.");
  let claims: { exp?: unknown };
  try {
    claims = JSON.parse(Buffer.from(payload, "base64url").toString("utf8")) as { exp?: unknown };
  } catch {
    throw new Error("Databricks OBO token contains invalid JWT claims.");
  }
  if (typeof claims.exp !== "number" || !Number.isFinite(claims.exp))
    throw new Error("Databricks OBO token does not contain a numeric expiry.");
  return Math.floor(claims.exp - nowMs / 1000);
}

function assertTruthy(value: unknown, message: string): asserts value {
  if (!value) throw new Error(message);
}

function isTerminalStatementState(state: string | undefined): boolean {
  return state === "SUCCEEDED" || state === "FAILED" || state === "CANCELED" || state === "CLOSED";
}

function sqlIdentifier(value: string): string {
  const parts = value.split(".");
  if (parts.length !== 3 || parts.some((part) => !/^[A-Za-z0-9_][A-Za-z0-9_$-]*$/.test(part))) {
    throw new Error(`Expected a three-part Unity Catalog identifier, received: ${value}`);
  }
  return parts.map((part) => `\`${part.replaceAll("`", "``")}\``).join(".");
}

async function canReadTable(
  tables: Pick<TablesClient, "getTable">,
  fullName: string,
): Promise<boolean> {
  try {
    await tables.getTable({ fullNameArg: fullName });
    return true;
  } catch (error) {
    if (
      databricksErrorStatusCode(error) === 403 ||
      /403|PERMISSION_DENIED|does not have/i.test(databricksErrorMessage(error))
    )
      return false;
    throw error;
  }
}
